// Who signs a checkpoint off — the toggles on the editor and the static tags on
// the preview, in one colour per role so the two read as the same thing.
import { cn } from '@my/ui';
import { ROLES, ROLE_META, type Role } from '../lib/model';

const ROLE_STYLE: Record<Role, { on: string; off: string }> = {
  planner: { on: 'bg-sky-600 text-white ring-sky-600', off: 'bg-white text-sky-700 ring-sky-200 hover:bg-sky-50' },
  driver: {
    on: 'bg-emerald-600 text-white ring-emerald-600',
    off: 'bg-white text-emerald-700 ring-emerald-200 hover:bg-emerald-50',
  },
  sub: { on: 'bg-amber-500 text-white ring-amber-500', off: 'bg-white text-amber-700 ring-amber-200 hover:bg-amber-50' },
};

export function RoleToggles({
  roles,
  onChange,
  plannerDisabled,
}: {
  roles: Role[];
  onChange: (next: Role[]) => void;
  /** The kind can only be done on site (a signature, a temperature). */
  plannerDisabled: boolean;
}) {
  const toggle = (r: Role) => {
    const next = roles.includes(r) ? roles.filter((x) => x !== r) : ROLES.filter((x) => x === r || roles.includes(x));
    // Somebody has to do it — the last role can't be switched off.
    if (next.length > 0) onChange(next);
  };

  return (
    <span className="inline-flex items-center gap-1">
      {ROLES.map((r) => {
        const on = roles.includes(r);
        const disabled = r === 'planner' && plannerDisabled && !on;
        return (
          <button
            key={r}
            type="button"
            aria-pressed={on}
            disabled={disabled}
            onClick={() => toggle(r)}
            title={disabled ? 'Only possible on site' : ROLE_META[r].detail}
            className={cn(
              'rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 transition-colors',
              on ? ROLE_STYLE[r].on : ROLE_STYLE[r].off,
              disabled && 'cursor-not-allowed opacity-40',
            )}
          >
            {ROLE_META[r].label}
          </button>
        );
      })}
    </span>
  );
}

export function RoleTag({ role }: { role: Role }) {
  return (
    <span
      title={ROLE_META[role].detail}
      className={cn('rounded-full px-1.5 py-px text-[9px] font-semibold ring-1', ROLE_STYLE[role].on)}
    >
      {ROLE_META[role].label}
    </span>
  );
}
